'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Status } from '~/components/Notification';
import { useCaptcha } from '~/hooks/useCaptcha';
import { useMarketingOptOut } from '~/hooks/useMarketingOptOut';
import { type ConciergeFormSchema, conciergeFormSchema } from '~/schemas/conciergeFormSchema';
import { trackEmailSubmitted, trackEvent } from '~/utils';
import { getCookie } from '~/utils/cookies';
import { getDisplayCurrencyCookie } from '~/utils/currencyHelpers';
import { formatPlacesList, toSentenceCase, toTitleCase } from '~/utils/formHelper';
import { isSingleOptInEnabled } from '~/utils/singleOptInFlag';
import { getTrackingParamsFromCookies } from '~/utils/tracking';
type AutofillPreset = 'solo' | 'couple' | 'family';
type SubmitStatus = {
  state: Status;
  message: string;
} | null;
export const useConciergeForm = () => {
  const t = useTranslations('content.feedback');
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(null);
  const showError = (message?: string) => setSubmitStatus({
    state: 'error',
    message: message || t('submit_error')
  });
  const showSuccess = () => setSubmitStatus({
    state: 'success',
    message: t('submit_success')
  });
  const locale = useLocale(); // auto-populated metadata hidden field: BCP-47 URL locale (en-GB/en-US/…)
  const searchParams = useSearchParams();
  const {
    captchaRef,
    onCaptchaErrored,
    executeCaptcha
  } = useCaptcha();
  const submitMarketingOptOut = useMarketingOptOut('concierge_email_capture');
  const singleOptIn = isSingleOptInEnabled();
  const {
    medium: cookieMedium,
    source: cookieSource,
    term: cookieTerm,
    campaign: cookieCampaign,
    campaignId: cookieCampaignId,
    gclid: cookieGclid
  } = getTrackingParamsFromCookies({
    __gtm_campaign_url: getCookie('__gtm_campaign_url')
  });
  const first = (...vals: Array<string | null | undefined>) => {
    for (const v of vals) {
      if (v && `${v}`.trim() && v !== 'null' && v !== 'undefined') return v;
    }
    return undefined;
  };

  // Campaign attribution metadata captured FE-only for now;
  // TODO: BE consumption tracking, locale, currency
  const tracking = useMemo(() => {
    const qp = (k: string) => searchParams.get(k);
    return {
      utm_source: first(qp('utm_source'), cookieSource),
      term: first(qp('term'), qp('utm_term'), cookieTerm),
      utm_campaign: first(qp('utm_campaign'), cookieCampaign),
      campaignid: first(qp('campaignid'), qp('campaign_id'), cookieCampaignId),
      gclid: first(qp('gclid'), cookieGclid),
      tripType: first(qp('tripId'), qp('SQF_TRIPID')),
      medium: first(qp('utm_medium'), cookieMedium)
    };
  }, [searchParams, cookieSource, cookieTerm, cookieCampaign, cookieCampaignId, cookieGclid, cookieMedium]);
  const methods = useForm<ConciergeFormSchema>({
    resolver: zodResolver(conciergeFormSchema, {
      errorMap: (issue, ctx) => {
        const path = issue.path?.join('.');
        const map: Record<string, string> = {
          whyGetIntouch: t('reason_required'),
          whereToGo: t('destination_required'),
          whenTravelling: t('date_required'),
          travelDuration: t('duration_required'),
          budget: t('budget_required'),
          firstName: t('name_first_required'),
          lastName: t('name_last_required'),
          email: issue.code === 'invalid_string' ? t('email_invalid_format') : t('email_required'),
          preferredContactMethod: t('contact_method_required'),
          leadPassengerCountry: t('country_required'),
          elderlyPassenger: t('elderly_required'),
          rooms: t('rooms_minimum'),
          adultCount: t('adult_minimum'),
          childCount: t('child_count_negative'),
          numberOfElderlyPassengers: t('elderly_count_negative'),
          phone: t('phone_required')
        };
        return {
          message: (path && map[path]) ?? ctx.defaultError
        };
      }
    }),
    defaultValues: {
      flexibility: '',
      firstName: '',
      lastName: '',
      email: '',
      preferredContactMethod: undefined,
      leadPassengerCountry: '' as ConciergeFormSchema['leadPassengerCountry'],
      budget: '',
      captcha: '',
      middleName: '',
      elderlyPassenger: '' as ConciergeFormSchema['elderlyPassenger'],
      numberOfElderlyPassengers: 0,
      howDidYouHearAboutUsDetail: '',
      childAges: '',
      howDidYouHearAboutUs: '',
      ...(singleOptIn ? {} : {
        newsletterSignup: false
      }),
      marketingOptOut: false,
      adultCount: 2,
      childCount: 0,
      whereToGo: '',
      whenTravelling: '',
      travelDuration: '',
      anythingElse: '',
      bestStation: '',
      extraPlaces: '',
      phone: '',
      whyGetIntouch: '' as ConciergeFormSchema['whyGetIntouch'],
      rooms: 1
    },
    shouldFocusError: true
  });
  const onSubmit = async (data: ConciergeFormSchema) => {
    setSubmitStatus(null); // clear any prior outcome on resubmit
    const normalized: ConciergeFormSchema = {
      ...data,
      whereToGo: toTitleCase(data.whereToGo),
      bestStation: toTitleCase(data.bestStation),
      extraPlaces: formatPlacesList(data.extraPlaces),
      anythingElse: toSentenceCase(data.anythingElse)
    };

    // Hidden field, display-currency cookie value (may be undefined → omitted from JSON).
    const currency = getDisplayCurrencyCookie();
    try {
      const captcha = await executeCaptcha();
      const res = await fetch('/api/contact/concierge', {
        method: 'POST',
        body: JSON.stringify({
          ...normalized,
          captcha,
          ...tracking,
          locale,
          currency
        }),
        headers: {
          'Content-type': 'application/json'
        }
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => null); // tolerate non-JSON error pages
        showError(errorData?.error);
        return;
      }
    } catch {
      // Network reject / aborted request / captcha failure — surface a generic error.
      showError();
      return;
    } finally {
      // Always re-arm the invisible reCAPTCHA so a retry isn't left hanging.
      captchaRef.current?.reset();
    }

    // Privacy policy is accepted implicitly by submitting the form (no longer a tick-box).
    const privacyPolicyAccepted = true;
    const newsletterSignup = singleOptIn ? !data.marketingOptOut : data.newsletterSignup;
    try {
      // Non-blocking side-effect; the support lead is already created.
      await submitMarketingOptOut({
        ...data,
        privacyPolicyAccepted,
        newsletterSignup
      });
    } catch {
      // Keep the success/reset path reachable if the opt-out call rejects.
    }
    trackEmailSubmitted(data.email, 'concierge_form', {
      marketingOptOut: data.marketingOptOut
    });
    trackEvent('concierge-page-submit', {
      event_category: 'concierge_form',
      event_label: 'Concierge Form Submitted',
      newsletter_signup: Boolean(newsletterSignup),
      marketing_opt_out: Boolean(data.marketingOptOut)
    });
    showSuccess();
    methods.reset();
  };
  const autofill = (preset: AutofillPreset = 'couple') => {
    const base = {
      whyGetIntouch: "I'd like a custom trip" as const,
      whereToGo: 'Spain',
      whenTravelling: '2026-01-01',
      travelDuration: '10 days',
      budget: '£2,000-£3,000',
      firstName: 'Test',
      lastName: 'Test',
      email: 'test@test.com',
      preferredContactMethod: 'email',
      leadPassengerCountry: 'United Kingdom' as ConciergeFormSchema['leadPassengerCountry'],
      elderlyPassenger: 'No',
      numberOfElderlyPassengers: 0,
      bestStation: 'London St Pancras',
      extraPlaces: 'Florence, Venice',
      anythingElse: 'We prefer scenic train routes and boutique hotels near city centres.',
      flexibility: '+3days',
      howDidYouHearAboutUs: 'other',
      howDidYouHearAboutUsDetail: '',
      phone: '',
      ...(singleOptIn ? {} : {
        newsletterSignup: true
      }),
      captcha: '',
      middleName: ''
    };
    const byPreset = preset === 'solo' ? {
      rooms: 1,
      adultCount: 1,
      childCount: 0,
      childAges: ''
    } : preset === 'family' ? {
      rooms: 2,
      adultCount: 2,
      childCount: 2,
      childAges: '6, 9',
      whyGetIntouch: 'Any other reason' as const
    } : {
      rooms: 1,
      adultCount: 2,
      childCount: 0,
      childAges: ''
    }; // couple

    methods.reset({
      ...methods.getValues(),
      ...base,
      ...byPreset
    } as ConciergeFormSchema, {
      keepDefaultValues: true
    });
  };
  return {
    ...methods,
    Controller,
    onSubmit,
    captchaRef,
    onCaptchaErrored,
    autofill,
    submitStatus
  };
};